import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Info,
  Trash2,
  RefreshCw,
  Plus,
  Minus,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { ImportRow, SellerGroup, PROFILE_OPTIONS } from './types';
import { batchUpdateSellerGroups } from './api';
import { ImportPreviewTable } from './ImportPreviewTable';

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void | Promise<void>;
  currentGroups: SellerGroup[];
};

function parseFile(file: File): Promise<ImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (json.length < 2) {
          reject(new Error('A planilha deve conter ao menos o cabeçalho e uma linha de dados.'));
          return;
        }

        const rows = json.slice(1);
        const parsed: ImportRow[] = rows.map((row) => {
          const groupId = (row[0] || '').toString().trim();
          const groupName = (row[1] || '').toString().trim();
          const profile = (row[2] || '').toString().trim();

          if (!groupId && !groupName && !profile) {
            return { groupId, groupName, profile, valid: false, reason: 'Linha vazia' };
          }

          if (!groupId) {
            return { groupId, groupName, profile, valid: false, reason: 'Group ID vazio' };
          }

          if (!groupName) {
            return { groupId, groupName, profile, valid: false, reason: 'Group Name vazio' };
          }

          if (!profile) {
            return { groupId, groupName, profile, valid: false, reason: 'Perfil vazio' };
          }

          const profileMatch = PROFILE_OPTIONS.find(
            (p) => p !== '' && p.toLowerCase() === profile.toLowerCase()
          );

          if (!profileMatch) {
            return {
              groupId,
              groupName,
              profile,
              valid: false,
              reason: `Perfil inválido: "${profile}"`,
            };
          }

          return { groupId, groupName, profile: profileMatch, valid: true };
        });

        const filtered = parsed.filter(
          (r) => !(r.reason === 'Linha vazia' && !r.groupId && !r.groupName && !r.profile)
        );

        resolve(filtered);
      } catch {
        reject(new Error('Erro ao ler o arquivo. Verifique se é um XLSX válido.'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

function enrichRowsWithChanges(rows: ImportRow[], groups: SellerGroup[]): ImportRow[] {
  const groupMap = new Map<string, SellerGroup>();
  groups.forEach((g) => groupMap.set(g.id, g));

  return rows.map((row) => {
    if (!row.valid) return row;

    const existing = groupMap.get(row.groupId);

    if (!existing) {
      return { ...row, changeType: 'new' };
    }

    if (existing.profile === row.profile) {
      return { ...row, changeType: 'unchanged', currentProfile: existing.profile };
    }

    return { ...row, changeType: 'changed', currentProfile: existing.profile };
  });
}

export const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  currentGroups,
}) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importDone, setImportDone] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setParsedRows([]);
    setParseError(null);
    setImporting(false);
    setImportProgress(0);
    setImportTotal(0);
    setImportDone(false);
    setImportErrors([]);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetState();
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, resetState]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, importing]);

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose();
  };

  const handleFile = async (f: File) => {
    setParseError(null);
    setImportDone(false);
    setImportErrors([]);

    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      setParseError('Formato inválido. Envie um arquivo .xlsx');
      return;
    }

    setFile(f);
    try {
      const rows = await parseFile(f);
      const enriched = enrichRowsWithChanges(rows, currentGroups);
      setParsedRows(enriched);
    } catch (err: any) {
      setParseError(err.message || 'Erro ao processar arquivo.');
      setParsedRows([]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const validRows = parsedRows.filter((r) => r.valid);
  const invalidRows = parsedRows.filter((r) => !r.valid);

  const changeCounts = useMemo(() => {
    const changed:any = validRows.filter((r) => r.changeType === 'changed').length;
    const newItems:any = validRows.filter((r) => r.changeType === 'new').length;
    const unchanged:any = validRows.filter((r) => r.changeType === 'unchanged').length;
    return { changed, new: newItems, unchanged };
  }, [validRows]);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    setImportTotal(validRows.length);
    setImportProgress(0);
    setImportErrors([]);

    try {
      const items = validRows.map((row) => ({
        group_id: row.groupId,
        profile: row.profile,
      }));

      const res = await batchUpdateSellerGroups(items);
      setImportProgress(validRows.length);

      if (!res.success) {
        const errors: string[] = [];
        if (res.results) {
          res.results
            .filter((r) => !r.ok)
            .forEach((r) => {
              errors.push(`Grupo ${r.group_id ?? items[r.index]?.group_id}: ${r.error || 'Erro desconhecido'}`);
            });
        }
        if (errors.length === 0) {
          errors.push(res.error || 'Erro ao processar a importação');
        }
        setImportErrors(errors);
      }
    } catch {
      setImportErrors(['Falha na conexão com o servidor']);
    }

    setImporting(false);
    setImportDone(true);
    await onImportComplete();
  };

  const progressPercent = importTotal > 0 ? Math.round((importProgress / importTotal) * 100) : 0;
  const importSuccessCount = importDone ? validRows.length - importErrors.length : 0;

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Importar planilha"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-[95vw] max-w-3xl max-h-[90vh] flex flex-col transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar Dados</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Faça upload de um arquivo XLSX com os dados dos grupos
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={importing}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 space-y-2">
                <p className="font-medium">Padrão da planilha esperado:</p>
                <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-blue-100/50">
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">Group ID</th>
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">Group Name</th>
                        <th className="px-3 py-2 text-left font-semibold text-blue-900">Perfil</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-blue-100">
                        <td className="px-3 py-1.5 text-blue-700 font-mono">150</td>
                        <td className="px-3 py-1.5 text-blue-700">Equipe Comercial</td>
                        <td className="px-3 py-1.5 text-blue-700">Seller</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Perfis válidos: <strong>Compra Agora</strong>, <strong>Indústria</strong> e <strong>Seller</strong>
                </p>
              </div>
            </div>
          </div>

          {!file && !importing && !importDone && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
              role="button"
              aria-label="Área de upload de arquivo"
            >
              <div
                className={`p-3 rounded-xl mb-3 ${
                  isDragging ? 'bg-blue-100' : 'bg-gray-200'
                }`}
              >
                <Upload
                  className={`w-6 h-6 ${isDragging ? 'text-blue-600' : 'text-gray-500'}`}
                />
              </div>
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? 'Solte o arquivo aqui' : 'Arraste ou clique para selecionar'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Apenas arquivos .xlsx</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleInputChange}
                className="hidden"
                aria-hidden="true"
              />
            </div>
          )}

          {parseError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{parseError}</p>
            </div>
          )}

          {file && parsedRows.length > 0 && !importDone && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                    {file.name}
                  </span>
                </div>
                {!importing && (
                  <button
                    onClick={resetState}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Remover
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-emerald-700 leading-tight">{validRows.length}</p>
                    <p className="text-[10px] text-emerald-600">Válidos</p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2.5">
                  <RefreshCw className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-amber-700 leading-tight">{changeCounts.changed}</p>
                    <p className="text-[10px] text-amber-600">Serão alterados</p>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2.5">
                  <Plus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-blue-700 leading-tight">{changeCounts.new}</p>
                    <p className="text-[10px] text-blue-600">Novos</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2.5">
                  <Minus className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-gray-600 leading-tight">{changeCounts.unchanged}</p>
                    <p className="text-[10px] text-gray-500">Sem alteração</p>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-base font-bold text-red-700 leading-tight">{invalidRows.length}</p>
                    <p className="text-[10px] text-red-600">Inválidos</p>
                  </div>
                </div>
              </div>

              {importing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Importando...
                    </span>
                    <span className="font-mono font-medium">
                      {importProgress}/{importTotal} ({progressPercent}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}

              <ImportPreviewTable rows={parsedRows} />
            </>
          )}

          {importDone && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className={`rounded-full p-4 mb-4 ${importErrors.length > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                {importErrors.length > 0 ? (
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                )}
              </div>
              <p className="text-base font-semibold text-gray-900">
                {importErrors.length > 0 ? 'Importação concluída com erros' : 'Importação concluída'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {importSuccessCount} {importSuccessCount === 1 ? 'item importado' : 'itens importados'} com sucesso
              </p>
              {importErrors.length > 0 && (
                <div className="mt-4 w-full bg-red-50 border border-red-200 rounded-xl p-3 max-h-[120px] overflow-y-auto">
                  {importErrors.map((err, i) => (
                    <p key={i} className="text-xs text-red-700">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <span className="text-xs text-gray-400">
            {!importing && 'Pressione ESC para fechar'}
          </span>
          <div className="flex items-center gap-3">
            {!importDone && (
              <button
                onClick={handleClose}
                disabled={importing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            {file && parsedRows.length > 0 && !importDone && (
              <button
                onClick={handleImport}
                disabled={validRows.length === 0 || importing}
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importar {validRows.length} {validRows.length === 1 ? 'item' : 'itens'}
                  </>
                )}
              </button>
            )}
            {importDone && (
              <button
                onClick={handleClose}
                className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all shadow-sm shadow-blue-600/20"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
